import { supabase } from '../lib/supabase';
import type { ExtracaoFaturaRow, ExtracaoResposta, ExtracaoLoteRow, OrigemExtracao, CorrecoesPlanilha } from '../../types';

const BUCKET = 'faturas-extraidas';

// CAMPOS_LISTA é o suficiente pra desenhar a tabela do Histórico. CAMPOS_COMPLETOS
// puxa `dados_estruturados` (JSONB grande) e só deve ser usado quando esse conteúdo
// vai ser realmente lido — Revisão, Planilha e exportação.
const CAMPOS_LISTA = 'id,created_at,origem,lote_id,arquivo_nome,uc_codigo,mes_referencia,nome_cliente,cpf_cnpj,grupo,total_pagar,status,score_auditoria,ocr_confidence,parser_version,pdf_storage_path,reconciliacao,usuario_nome,corrigido_em,corrigido_por_nome';
const CAMPOS_COMPLETOS = `${CAMPOS_LISTA},dados_estruturados,correcoes_planilha`;

/** Origens exibidas como abas no Histórico, na ordem em que aparecem. */
export const ORIGENS_EXTRACAO: OrigemExtracao[] = [
  'gestao', 'robo-manual', 'robo-assinatura', 'robo-historico', 'analise-cadastral', 'crm',
];

export interface FiltrosHistorico {
  busca?: string;          // UC, nome ou CPF/CNPJ
  status?: 'ok' | 'revisar' | 'falhou';
  grupo?: 'A' | 'B';
  origem?: OrigemExtracao;
  mes_referencia?: string; // 'YYYY-MM'
  dataDe?: string;         // ISO
  dataAte?: string;        // ISO
}

/** Envia um PDF para a Edge Function determinística e devolve estruturado + reconciliação.
 *  `loteId`: reaproveitado pra todos os arquivos de um mesmo upload, agrupa no Histórico.
 *  `origem`: rótulo de auditoria (default 'gestao' na função). Passar
 *  'analise-cadastral' quando a chamada vier da Análise Técnica do wizard. */
export async function extrairFatura(
  file: File,
  loteId?: string | null,
  origem?: OrigemExtracao,
): Promise<ExtracaoResposta> {
  const form = new FormData();
  form.append('fatura_padrao', file);
  if (loteId) form.append('lote_id', loteId);
  if (origem) form.append('origem', origem);
  const { data, error } = await supabase.functions.invoke('extrair-fatura', { body: form });
  if (error) {
    // Erros HTTP da função vêm em error.context; tenta extrair a mensagem amigável.
    let msg = error.message || 'Falha ao chamar a extração.';
    try {
      const body = await (error as any).context?.json?.();
      if (body?.error) msg = body.error;
    } catch { /* ignore */ }
    return { ok: false, error: msg };
  }
  return data as ExtracaoResposta;
}

/** Reprocessa uma extração existente reaproveitando o OCR salvo (sem novo custo de OCR). */
export async function reprocessarExtracao(id: string): Promise<ExtracaoResposta> {
  const { data, error } = await supabase.functions.invoke('extrair-fatura', { body: { reprocessar_id: id } });
  if (error) return { ok: false, error: error.message };
  return data as ExtracaoResposta;
}

/** Lista o histórico de extrações com filtros e paginação (respeita o teto de 1000 linhas). */
export async function listExtracoes(filtros: FiltrosHistorico = {}, limite = 500): Promise<ExtracaoFaturaRow[]> {
  let query = supabase
    .from('extracoes_faturas')
    .select(CAMPOS_LISTA)
    .order('created_at', { ascending: false })
    .limit(limite);

  if (filtros.status) query = query.eq('status', filtros.status);
  if (filtros.grupo) query = query.eq('grupo', filtros.grupo);
  if (filtros.origem) query = query.eq('origem', filtros.origem);
  if (filtros.mes_referencia) query = query.eq('mes_referencia', filtros.mes_referencia);
  if (filtros.dataDe) query = query.gte('created_at', filtros.dataDe);
  if (filtros.dataAte) query = query.lte('created_at', filtros.dataAte);
  if (filtros.busca && filtros.busca.trim()) {
    const b = `%${filtros.busca.trim()}%`;
    query = query.or(`uc_codigo.ilike.${b},nome_cliente.ilike.${b},cpf_cnpj.ilike.${b}`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as ExtracaoFaturaRow[];
}

/** Busca uma extração completa (inclui dados_estruturados + ocr não). */
export async function getExtracao(id: string): Promise<ExtracaoFaturaRow | null> {
  const { data, error } = await supabase
    .from('extracoes_faturas')
    .select(CAMPOS_COMPLETOS)
    .eq('id', id)
    .single();
  if (error) throw error;
  return (data as ExtracaoFaturaRow) ?? null;
}

/** Gera uma URL assinada (1h) para abrir o PDF original no Storage. */
export async function getPdfUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

/** Baixa os bytes do PDF original do Storage (para o ZIP do lote). */
export async function getPdfBlob(path: string | null): Promise<Blob | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage.from(BUCKET).download(path);
  if (error) return null;
  return data;
}

/** Resumo por lote (agrupamento de upload) para o Histórico, via view que agrega ao vivo. */
export async function listLotes(limite = 200): Promise<ExtracaoLoteRow[]> {
  const { data, error } = await supabase
    .from('extracoes_lotes_resumo')
    .select('id,created_at,usuario_nome,qtd_total,qtd_ok,qtd_revisar,qtd_falhou')
    .order('created_at', { ascending: false })
    .limit(limite);
  if (error) throw error;
  return (data || []) as ExtracaoLoteRow[];
}

/** Faturas de um lote específico (inclui dados_estruturados — usado no drill-down e na exportação em lote). */
export async function listExtracoesDoLote(loteId: string): Promise<ExtracaoFaturaRow[]> {
  // Pagina além do teto de 1000 linhas do PostgREST — um lote grande passa disso
  // fácil e a recuperação de sessão perderia faturas em silêncio.
  const PAGINA = 1000;
  const todas: ExtracaoFaturaRow[] = [];
  for (let de = 0; ; de += PAGINA) {
    const { data, error } = await supabase
      .from('extracoes_faturas')
      .select(CAMPOS_COMPLETOS)
      .eq('lote_id', loteId)
      .order('created_at', { ascending: true })
      .range(de, de + PAGINA - 1);
    if (error) throw error;
    todas.push(...((data || []) as ExtracaoFaturaRow[]));
    if (!data || data.length < PAGINA) break;
  }
  return todas;
}

/** Extrações da Gestão feitas antes do agrupamento por lote existir (origem='gestao'
 *  + lote_id nulo). Antes esta função trazia QUALQUER linha sem lote — o que incluía
 *  todo o tráfego dos robôs, que nunca mandam lote_id — e por isso o card "Extrações
 *  antigas (sem lote)" virou a caixa de entrada dos robôs. Agora cada origem tem a
 *  sua aba e aqui sobra só o legado real da Gestão. */
export async function listExtracoesGestaoSemLote(limite = 500): Promise<ExtracaoFaturaRow[]> {
  const { data, error } = await supabase
    .from('extracoes_faturas')
    .select(CAMPOS_LISTA)
    .eq('origem', 'gestao')
    .is('lote_id', null)
    .order('created_at', { ascending: false })
    .limit(limite);
  if (error) throw error;
  return (data || []) as ExtracaoFaturaRow[];
}

const PAGINA_ORIGEM = 200;

/** Uma página de extrações de uma origem. Devolve `temMais` pra alimentar o
 *  "Carregar mais" — sem isso a lista trunca em silêncio no limite (a origem
 *  'gestao' sozinha já passa de 2.600 linhas). */
export async function listExtracoesPorOrigem(
  origem: OrigemExtracao,
  pagina = 0,
  tamanho = PAGINA_ORIGEM,
): Promise<{ rows: ExtracaoFaturaRow[]; temMais: boolean }> {
  const de = pagina * tamanho;
  const { data, error } = await supabase
    .from('extracoes_faturas')
    .select(CAMPOS_LISTA)
    .eq('origem', origem)
    .order('created_at', { ascending: false })
    .range(de, de + tamanho - 1);
  if (error) throw error;
  const rows = (data || []) as ExtracaoFaturaRow[];
  return { rows, temMais: rows.length === tamanho };
}

/** Contagem por origem para os badges das abas. `head: true` não traz linha
 *  alguma — só o total do count, então é barato mesmo em origem com milhares. */
export async function contarPorOrigem(): Promise<Record<OrigemExtracao, number>> {
  const pares = await Promise.all(ORIGENS_EXTRACAO.map(async (origem) => {
    const { count, error } = await supabase
      .from('extracoes_faturas')
      .select('id', { count: 'exact', head: true })
      .eq('origem', origem);
    if (error) return [origem, 0] as const;
    return [origem, count ?? 0] as const;
  }));
  return Object.fromEntries(pares) as Record<OrigemExtracao, number>;
}

/** Uma extração a gravar: a camada de correção INTEIRA (já mesclada pelo chamador) e
 *  os escalares denormalizados que precisam acompanhar a correção. */
export interface CorrecaoExtracao {
  id: string;
  correcoes: CorrecoesPlanilha;
  escalares?: Partial<Pick<ExtracaoFaturaRow, 'uc_codigo' | 'nome_cliente' | 'cpf_cnpj' | 'mes_referencia' | 'grupo' | 'total_pagar'>>;
}

/** Grava as correções manuais da planilha. Uma linha por extração — o RLS só deixa
 *  'adm'/'analista' escrever, e o GRANT de coluna limita o que pode ser tocado
 *  (nada de dados_estruturados/status/reconciliacao pelo front).
 *  Devolve os ids que falharam em vez de estourar no primeiro erro: numa planilha de
 *  lote é melhor gravar o que dá e dizer o que faltou. */
export async function salvarCorrecoesPlanilha(
  itens: CorrecaoExtracao[],
  autor?: { id?: string | null; nome?: string | null },
): Promise<{ salvos: number; falhas: { id: string; erro: string }[] }> {
  const agora = new Date().toISOString();
  const falhas: { id: string; erro: string }[] = [];
  let salvos = 0;

  for (const item of itens) {
    const patch: Record<string, unknown> = {
      ...(item.escalares || {}),
      correcoes_planilha: item.correcoes,
      corrigido_em: agora,
      corrigido_por_id: autor?.id ?? null,
      corrigido_por_nome: autor?.nome ?? null,
    };
    const { data, error } = await supabase
      .from('extracoes_faturas')
      .update(patch)
      .eq('id', item.id)
      .select('id');
    // UPDATE barrado por RLS não é erro: volta 0 linhas em silêncio.
    if (error) falhas.push({ id: item.id, erro: error.message });
    else if (!data || data.length === 0) falhas.push({ id: item.id, erro: 'sem permissão para salvar (RLS) ou extração não encontrada' });
    else salvos++;
  }
  return { salvos, falhas };
}

/** Carrega `dados_estruturados` de várias linhas de uma vez — a tabela lista sem
 *  esse JSONB (leve), e Planilha/Excel precisam dele só na hora de exportar. */
export async function carregarEstruturados(ids: string[]): Promise<ExtracaoFaturaRow[]> {
  if (!ids.length) return [];
  const LOTE = 200;
  const todas: ExtracaoFaturaRow[] = [];
  for (let i = 0; i < ids.length; i += LOTE) {
    const { data, error } = await supabase
      .from('extracoes_faturas')
      .select(CAMPOS_COMPLETOS)
      .in('id', ids.slice(i, i + LOTE));
    if (error) throw error;
    todas.push(...((data || []) as ExtracaoFaturaRow[]));
  }
  return todas;
}
